import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  History, Download, RefreshCw, Search, Filter, AlertCircle,
  User, Shield, Cpu, ChevronLeft, ChevronRight, Loader2,
} from "lucide-react";
import axios from "axios";

const API_URL = import.meta.env.VITE_API_URL || "http://localhost:3944";

interface AuditEvent {
  id: number;
  actor: string;
  actor_type: "merchant" | "admin" | "system";
  event: string;
  target: string;
  metadata: Record<string, any> | null;
  ip_address: string;
  user_agent: string;
  created_at: string | null;
}

interface ListResponse {
  total: number;
  limit: number;
  offset: number;
  events_available: string[];
  items: AuditEvent[];
}

const PER_PAGE = 25;

const authHeaders = () => {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const fmtDate = (iso: string | null): string => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("fr-FR", {
      year: "numeric", month: "2-digit", day: "2-digit",
      hour: "2-digit", minute: "2-digit", second: "2-digit",
    });
  } catch { return iso; }
};

const humanizeEvent = (ev: string): string => {
  // LOGIN_SUCCESS -> Login success
  return ev.replace(/_/g, " ").toLowerCase().replace(/^./, c => c.toUpperCase());
};

const actorBadge = (type: string) => {
  const style: Record<string, { bg: string; fg: string; Icon: any; label: string }> = {
    merchant: { bg: "bg-indigo-100", fg: "text-indigo-800", Icon: User, label: "Merchant" },
    admin:    { bg: "bg-slate-100",  fg: "text-slate-700", Icon: Shield, label: "Admin" },
    system:   { bg: "bg-amber-100",  fg: "text-amber-800", Icon: Cpu, label: "System" },
  };
  const s = style[type] || style.merchant;
  const Icon = s.Icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${s.bg} ${s.fg}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
};

const AuditLog: React.FC = () => {
  const [items, setItems] = useState<AuditEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [eventsAvailable, setEventsAvailable] = useState<string[]>([]);

  const [filterEvent, setFilterEvent] = useState("");
  const [filterActor, setFilterActor] = useState("");
  const [filterFrom, setFilterFrom] = useState("");
  const [filterTo, setFilterTo] = useState("");
  const [downloading, setDownloading] = useState(false);
  const [expandedRow, setExpandedRow] = useState<number | null>(null);

  const fetchLog = useCallback(async (silent = false) => {
    if (silent) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const params: Record<string, string | number> = { limit: PER_PAGE, offset };
      if (filterEvent) params.event = filterEvent;
      if (filterActor) params.actor = filterActor;
      if (filterFrom)  params.date_from = filterFrom;
      if (filterTo)    params.date_to = filterTo;
      const res = await axios.get<ListResponse>(`${API_URL}/settings/audit-log`, {
        headers: authHeaders(), params,
      });
      setItems(res.data.items || []);
      setTotal(res.data.total || 0);
      setEventsAvailable(res.data.events_available || []);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load audit log");
    } finally {
      setLoading(false); setRefreshing(false);
    }
  }, [offset, filterEvent, filterActor, filterFrom, filterTo]);

  useEffect(() => { fetchLog(false); }, [fetchLog]);

  const downloadCsv = async () => {
    setDownloading(true);
    try {
      const params: Record<string, string> = {};
      if (filterEvent) params.event = filterEvent;
      if (filterActor) params.actor = filterActor;
      if (filterFrom)  params.date_from = filterFrom;
      if (filterTo)    params.date_to = filterTo;
      const res = await axios.get(`${API_URL}/settings/audit-log/export.csv`, {
        headers: authHeaders(), params, responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      const now = new Date().toISOString().slice(0, 19).replace(/[T:]/g, "-");
      a.download = `audit-log_${now}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      setError(e?.response?.data?.detail || e?.message || "Failed to download CSV");
    } finally {
      setDownloading(false);
    }
  };

  const resetFilters = () => {
    setFilterEvent(""); setFilterActor(""); setFilterFrom(""); setFilterTo("");
    setOffset(0);
  };

  const currentPage = Math.floor(offset / PER_PAGE) + 1;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));

  const activeFilters = useMemo(() => {
    return [filterEvent, filterActor, filterFrom, filterTo].filter(Boolean).length;
  }, [filterEvent, filterActor, filterFrom, filterTo]);

  return (
    <div className="max-w-6xl mx-auto px-4 py-6 space-y-6">
      {/* Hero */}
      <div
        className="relative overflow-hidden rounded-2xl p-7 text-white shadow-xl shadow-blue-500/30"
        style={{ background: "linear-gradient(135deg, #3b82f6 0%, #2563eb 55%, #1d4ed8 100%)" }}
      >
        <div className="pointer-events-none absolute -right-16 -top-16 w-64 h-64 rounded-full blur-3xl bg-sky-300/35" />
        <div className="pointer-events-none absolute -left-10 bottom-0 w-48 h-48 rounded-full blur-3xl bg-indigo-300/25" />
        <div className="pointer-events-none absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)",
          backgroundSize: "24px 24px",
        }} />
        <div className="relative flex items-start justify-between gap-4 flex-wrap">
          <div className="flex items-start gap-4 min-w-0">
            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm ring-1 ring-white/30 flex items-center justify-center flex-shrink-0 shadow-lg shadow-black/10">
              <History className="w-7 h-7 text-white" strokeWidth={2.2} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm rounded-full px-3 py-1 mb-2 ring-1 ring-white/20">
                <div className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                <span className="text-[11px] font-semibold text-white/90 tracking-wide uppercase">Security trail</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Audit Log</h1>
              <p className="text-sm text-white/85 mt-1.5 max-w-2xl leading-relaxed">
                Every security-sensitive action on your account: logins, user changes,
                API-key rotations, webhook edits, IP whitelist requests, refunds and payouts.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => fetchLog(true)}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-xl bg-white/15 backdrop-blur-sm border border-white/25 text-white text-sm font-semibold hover:bg-white/25 disabled:opacity-50 transition-all"
            >
              <RefreshCw className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`} />
              Refresh
            </button>
            <button
              onClick={downloadCsv}
              disabled={downloading || total === 0}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white text-blue-700 text-sm font-semibold shadow-lg shadow-black/10 hover:shadow-xl hover:-translate-y-0.5 disabled:opacity-50 transition-all"
            >
              {downloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
              Export CSV
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-4">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Event</label>
            <select
              value={filterEvent}
              onChange={e => { setFilterEvent(e.target.value); setOffset(0); }}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:ring-2 focus:ring-emerald-500"
            >
              <option value="">All events</option>
              {eventsAvailable.map(ev => (
                <option key={ev} value={ev}>{humanizeEvent(ev)}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Actor</label>
            <input
              type="text"
              value={filterActor}
              onChange={e => { setFilterActor(e.target.value); setOffset(0); }}
              placeholder="Email or username…"
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From</label>
            <input
              type="date"
              value={filterFrom}
              onChange={e => { setFilterFrom(e.target.value); setOffset(0); }}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To</label>
            <input
              type="date"
              value={filterTo}
              onChange={e => { setFilterTo(e.target.value); setOffset(0); }}
              className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="flex items-end">
            {activeFilters > 0 && (
              <button
                onClick={resetFilters}
                className="inline-flex items-center gap-1 px-3 py-2 text-xs rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50"
              >
                <Filter className="w-3 h-3" /> Reset ({activeFilters})
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-slate-400">
            <Loader2 className="w-6 h-6 animate-spin mx-auto" />
          </div>
        ) : error ? (
          <div className="p-6">
            <div className="rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm p-3 flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5" /> {error}
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center text-slate-400 text-sm">
            {activeFilters > 0
              ? "No events match these filters."
              : "No events recorded for your account yet."}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">When (UTC)</th>
                  <th className="px-4 py-3 text-left font-semibold">Event</th>
                  <th className="px-4 py-3 text-left font-semibold">Actor</th>
                  <th className="px-4 py-3 text-left font-semibold">Target</th>
                  <th className="px-4 py-3 text-left font-semibold">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((e) => (
                  <React.Fragment key={e.id}>
                    <tr
                      className="hover:bg-slate-50/60 cursor-pointer"
                      onClick={() => setExpandedRow(expandedRow === e.id ? null : e.id)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap font-mono text-xs text-slate-600">
                        {fmtDate(e.created_at)}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-block px-2 py-0.5 rounded-md text-xs font-semibold bg-slate-100 text-slate-800 font-mono">
                          {e.event}
                        </span>
                        <div className="text-xs text-slate-500 mt-0.5">{humanizeEvent(e.event)}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          {actorBadge(e.actor_type)}
                          <span className="text-slate-700 text-sm">{e.actor}</span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-slate-700 text-sm max-w-[240px] truncate" title={e.target}>
                        {e.target || <span className="text-slate-400">—</span>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-slate-600">
                        {e.ip_address || <span className="text-slate-400">—</span>}
                      </td>
                    </tr>
                    {expandedRow === e.id && e.metadata && (
                      <tr className="bg-slate-50/50">
                        <td colSpan={5} className="px-6 py-3">
                          <div className="text-xs text-slate-500 mb-1 font-semibold uppercase tracking-wider">Metadata</div>
                          <pre className="bg-slate-900 text-emerald-200 text-xs p-3 rounded-lg overflow-x-auto font-mono">
{JSON.stringify(e.metadata, null, 2)}
                          </pre>
                          {e.user_agent && (
                            <div className="mt-2 text-[11px] text-slate-500">
                              <span className="font-semibold">User agent:</span> {e.user_agent}
                            </div>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {items.length > 0 && (
          <div className="p-4 border-t border-slate-100 flex items-center justify-between text-sm text-slate-600">
            <span>
              Showing <span className="font-semibold">{offset + 1}</span>–
              <span className="font-semibold">{Math.min(offset + PER_PAGE, total)}</span> of{" "}
              <span className="font-semibold">{total}</span>
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setOffset(Math.max(0, offset - PER_PAGE))}
                disabled={offset === 0}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" /> Prev
              </button>
              <span className="text-xs text-slate-500">
                Page {currentPage} / {totalPages}
              </span>
              <button
                onClick={() => setOffset(offset + PER_PAGE)}
                disabled={offset + PER_PAGE >= total}
                className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-200 rounded-lg disabled:opacity-40"
              >
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLog;
